import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { ArrowLeft, Wind, Check, Loader2 } from "lucide-react";

const PROMPTS = [
  { id: "physical_holding", label: "The Physical Holding", q: "Where does your body notice tension or bracing right now, even when you are trying to rest? What was happening just before it appeared?" },
  { id: "unspoken_weight", label: "The Unspoken Weight", q: "What have you been carrying that you rarely, if ever, say out loud to anyone?" },
  { id: "daily_cost", label: "The Daily Cost", q: "What does carrying this endurance ask of your energy, your relationships, or your capacity to be present?" },
  { id: "space_of_unfolding", label: "The Space of Unfolding", q: "What might soften or become possible if you did not have to hold all of this alone?" },
  { id: "next_gentle_step", label: "The Next Gentle Step", q: "What kind of support would feel safe enough for your nervous system to receive right now?" },
];
const LS = "bnm-reflection-draft";
const MOODS = ["Exhausted", "Hopeful", "Reflective", "Overwhelmed", "Tender", "Steady"];

export default function Reflect() {
  const [answers, setAnswers] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [step, setStep] = useState(0);
  const [share, setShare] = useState(true);
  const [form, setForm] = useState({ reader_name: "", reader_email: "", phone: "", mood: "" });
  const [wantsFollowup, setWantsFollowup] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    try { const d = JSON.parse(localStorage.getItem(LS) || "{}"); if (d.answers) setAnswers(d.answers); if (d.form) setForm(d.form); } catch (e) {}
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { try { localStorage.setItem(LS, JSON.stringify({ answers, form })); } catch (e) {} }, 300);
    return () => clearTimeout(t);
  }, [answers, form]);

  const setAns = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async () => {
    setSending(true); setErr("");
    try {
      // "Keep private" stays entirely on this device — nothing is sent to Casey.
      if (!share) {
        localStorage.setItem(LS, JSON.stringify({ answers, form }));
        setDone(true);
        return;
      }
      await axios.post(`${API}/reflections/submit`, {
        reader_name: form.reader_name, reader_email: form.reader_email, phone: form.phone,
        mood: form.mood, responses: answers, wants_followup: wantsFollowup, keep_private: false,
      });
      localStorage.removeItem(LS);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Something interrupted the sending. Please try again.");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center watercolor-wash px-5 safe-top safe-bottom">
        <div className="max-w-md rounded-2xl bg-ivory-50 p-8 text-center shadow-xl ring-1 ring-ivory-400/50" data-testid="reflect-done">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage/25 text-ink"><Check className="h-6 w-6" /></span>
          <h1 className="mt-4 font-display text-2xl text-ink">Your words have been received, and held with care.</h1>
          <p className="mt-2 text-sm text-ink-soft">{share ? "Casey will witness them gently. If you asked for a Braced Call, he'll reach out." : "This reflection stayed with you."}</p>
          <Link to="/" className="debossed-btn mt-6 inline-flex w-full items-center justify-center rounded-full px-6 py-3 font-display text-ink">Return to the book</Link>
        </div>
      </div>
    );
  }

  const renderPrompt = (p) => (
    <div key={p.id} className="mb-8" data-testid={`reflect-prompt-${p.id}`}>
      <p className="kicker text-terracotta">{p.label}</p>
      <p className="mt-2 font-display text-lg leading-relaxed text-ink">{p.q}</p>
      <textarea
        value={answers[p.id] || ""}
        onChange={(e) => setAns(p.id, e.target.value)}
        data-testid={`reflect-input-${p.id}`}
        rows={4}
        placeholder="Take your time…"
        className="reflect-field mt-3 w-full resize-y rounded-xl border border-ivory-400/70 bg-[#F6F2EC] p-4 text-[15px] leading-relaxed text-ink transition focus:border-sage focus:shadow-[0_0_0_3px_rgba(152,168,152,0.25)] focus:outline-none"
      />
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#E8E2D9 100%)" }}>
      <div className="mx-auto max-w-2xl px-5 py-12 safe-top safe-bottom safe-x sm:px-8">
        <Link to="/" data-testid="reflect-back" className="mb-8 inline-flex items-center gap-2 font-display text-sm text-ink-soft hover:text-terracotta"><ArrowLeft className="h-4 w-4" /> Back to the book</Link>
        <p className="kicker text-ink-muted">A Quiet Inventory · Embodied Pages</p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">Set it down, one breath at a time.</h1>
        <p className="mt-3 text-[15px] text-ink-soft">Take your time. No word limit, no performance.</p>

        <div className="mt-5 flex gap-2 text-xs">
          <button onClick={() => setExpanded(false)} data-testid="mode-paced" className={`rounded-full px-4 py-1.5 font-display ${!expanded ? "bg-ink text-ivory-50" : "border border-ink/20 text-ink-soft"}`}>Paced</button>
          <button onClick={() => setExpanded(true)} data-testid="mode-expanded" className={`rounded-full px-4 py-1.5 font-display ${expanded ? "bg-ink text-ivory-50" : "border border-ink/20 text-ink-soft"}`}>All prompts</button>
        </div>

        <div className="mt-8">
          {expanded ? (
            PROMPTS.map((p) => renderPrompt(p))
          ) : (
            <>
              {renderPrompt(PROMPTS[step])}
              <div className="flex items-center justify-between">
                <button disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="rounded-full px-4 py-2 text-sm text-ink-soft disabled:opacity-40">Back</button>
                <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted"><Wind className="h-3.5 w-3.5" /> breathe · {step + 1} of {PROMPTS.length}</span>
                {step < PROMPTS.length - 1
                  ? <button onClick={() => setStep((s) => s + 1)} data-testid="reflect-next" className="rounded-full bg-ink px-5 py-2 text-sm text-ivory-50">Next</button>
                  : <span className="w-16" />}
              </div>
            </>
          )}
        </div>

        {(expanded || step === PROMPTS.length - 1) && (
          <div className="mt-10 rounded-2xl bg-[#ECE7DE] p-6" data-testid="reflect-submit-panel">
            <div className="flex gap-2">
              <button onClick={() => setShare(true)} data-testid="share-toggle" className={`flex-1 rounded-xl px-4 py-3 text-sm ${share ? "bg-sage/30 ring-1 ring-sage" : "bg-ivory-50"}`}>Share with Casey<br /><span className="text-xs text-ink-muted">witnessed with care</span></button>
              <button onClick={() => setShare(false)} data-testid="private-toggle" className={`flex-1 rounded-xl px-4 py-3 text-sm ${!share ? "bg-sage/30 ring-1 ring-sage" : "bg-ivory-50"}`}>Keep private<br /><span className="text-xs text-ink-muted">for your own journey</span></button>
            </div>

            {share && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <input value={form.reader_name} onChange={(e) => setForm({ ...form, reader_name: e.target.value })} placeholder="Preferred name" data-testid="reflect-name" className="rounded-lg border border-ivory-400/70 bg-ivory-50 p-2.5 text-sm text-ink" />
                <input value={form.reader_email} onChange={(e) => setForm({ ...form, reader_email: e.target.value })} placeholder="Email" data-testid="reflect-email" className="rounded-lg border border-ivory-400/70 bg-ivory-50 p-2.5 text-sm text-ink" />
                <select value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} data-testid="reflect-mood" className="rounded-lg border border-ivory-400/70 bg-ivory-50 p-2.5 text-sm text-ink">
                  <option value="">How are you arriving? (optional)</option>
                  {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="reflect-phone" className="rounded-lg border border-ivory-400/70 bg-ivory-50 p-2.5 text-sm text-ink" />
                <label className="flex items-center gap-2 text-sm text-ink-soft sm:col-span-2"><input type="checkbox" checked={wantsFollowup} onChange={(e) => setWantsFollowup(e.target.checked)} data-testid="reflect-followup" /> I'd like Casey to reach out about a free Braced Call or guidance.</label>
              </div>
            )}

            {err && <p className="mt-3 text-sm text-terracotta" data-testid="reflect-error">{err}</p>}
            <button onClick={submit} disabled={sending} data-testid="reflect-submit" className="debossed-btn mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-ink disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{share ? "Offer this to Casey" : "Keep this reflection"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
